import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  readConsent,
  writeConsent,
  clearOptionalCookies,
  hasConsent as _hasConsent,
  buildAcceptAll,
  buildRejectNonEssential,
  buildCustom,
  fetchBannerConfig,
  type CookieConsent,
  type ConsentCategory,
} from '@/lib/cookieConsent';
import type { CookieBannerConfig } from '@/types/bannerConfig';
import { BANNER_DEFAULTS } from '@/types/bannerConfig';

// ── Server-side consent telemetry ──────────────────────────────────────────────

/**
 * Fire-and-forget consent event reporting via `navigator.sendBeacon`.
 *
 * - Same-origin POST to `/api/v1/consent` — no CORS, no CSRF token needed.
 * - `domain` is derived server-side from `Host` header (not sent from client).
 * - Survives page unload (beacon semantics).
 * - Silently swallows all errors — consent UX must never depend on API availability.
 */
function reportConsent(
  consent: CookieConsent,
  action: 'accept_all' | 'reject_non_essential' | 'customize',
): void {
  try {
    const payload = JSON.stringify({
      consent_version: consent.version,
      consent_action: action,
      essential: consent.essential,
      functional: consent.functional,
      analytics: consent.analytics,
      preferences: consent.preferences,
      page_path: window.location.pathname,
      consented_at: consent.timestamp,
    });
    navigator.sendBeacon(
      '/api/v1/consent',
      new Blob([payload], { type: 'application/json' }),
    );
  } catch {
    /* silently swallow — consent UX must never depend on API */
  }
}

// ── Context value ──────────────────────────────────────────────────────────────

export interface CookieConsentValue {
  /** Current consent record, or null if no decision yet */
  consent: CookieConsent | null;
  /** True when the user has not yet made a consent decision */
  showBanner: boolean;
  /** Accept all categories */
  acceptAll: () => void;
  /** Reject all non-essential categories */
  rejectNonEssential: () => void;
  /** Save custom category choices */
  customize: (opts: Pick<CookieConsent, 'functional' | 'analytics' | 'preferences'>) => void;
  /** Check if a specific category has consent */
  hasConsent: (category: ConsentCategory) => boolean;
  /** Open the preferences sheet (for "Manage cookies" links) */
  openPreferences: () => void;
  /** Close the preferences sheet */
  closePreferences: () => void;
  /** Whether the preferences sheet is open */
  preferencesOpen: boolean;
  /** Remote banner configuration (position, theme, copy, etc.) */
  bannerConfig: CookieBannerConfig;
}

const CookieConsentContext = createContext<CookieConsentValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(() => readConsent());
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [bannerConfig, setBannerConfig] = useState<CookieBannerConfig>(BANNER_DEFAULTS);

  // Fetch remote banner config on mount (non-blocking — banner renders with defaults first)
  useEffect(() => {
    fetchBannerConfig().then(setBannerConfig).catch(() => {/* fallback already set */});
  }, []);

  const acceptAll = useCallback(() => {
    const c = buildAcceptAll();
    writeConsent(c);
    reportConsent(c, 'accept_all');
    setConsent(c);
  }, []);

  const rejectNonEssential = useCallback(() => {
    const c = buildRejectNonEssential();
    writeConsent(c);
    reportConsent(c, 'reject_non_essential');
    clearOptionalCookies();
    setConsent(c);
  }, []);

  const customize = useCallback(
    (opts: Pick<CookieConsent, 'functional' | 'analytics' | 'preferences'>) => {
      const c = buildCustom(opts);
      writeConsent(c);
      reportConsent(c, 'customize');
      // Clear optional artifacts for any category that was just disabled
      if (!opts.functional) {
        clearOptionalCookies();
      }
      setConsent(c);
    },
    [],
  );

  const hasConsent = useCallback(
    (category: ConsentCategory): boolean => {
      if (category === 'essential') return true;
      return consent?.[category] === true;
    },
    [consent],
  );

  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  // Show banner when: no consent yet, or remote config bumped consentVersion
  const showBanner = consent === null || consent.version < bannerConfig.consentVersion;

  const value = useMemo<CookieConsentValue>(
    () => ({
      consent,
      showBanner,
      acceptAll,
      rejectNonEssential,
      customize,
      hasConsent,
      openPreferences,
      closePreferences,
      preferencesOpen,
      bannerConfig,
    }),
    [consent, showBanner, acceptAll, rejectNonEssential, customize, hasConsent, openPreferences, closePreferences, preferencesOpen, bannerConfig],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useCookieConsent(): CookieConsentValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return ctx;
}

/**
 * Standalone check (no context needed) — for use in non-React code
 * or modules that run before the provider mounts.
 */
export { _hasConsent as hasConsentStandalone };
